import type {
	DeckCategoryFilter,
	DeckCategoryOption,
} from "src/api/flashcardDecks";
import isPes from "src/helpers/isPes";
import type {
	SearchDeck as Deck,
	DeckSortBy,
	DeckSortOrder,
	SearchDecksResponse,
} from "src/hooks/useFlashcards";
import { buildQuery, http } from "src/newApi";

export type SearchType = "questions" | "articles" | "decks";

export type QuickFilter = "starred" | "wrongAnswers" | "isUnanswered";

export type SnippetSource =
	| "title"
	| "question"
	| "answer_a"
	| "answer_b"
	| "answer_c"
	| "answer_d"
	| "answer_e"
	| "content"
	| "description"
	| "flashcardFront"
	| "flashcardBack";

export type SearchSnippet = {
	text: string;
	matchStart: number;
	matchLength: number;
	source: SnippetSource;
};

export type SearchQuestion = {
	id: number;
	pytanie: string;
	dzial?: string;
	termin?: string;
	snippets?: SearchSnippet[];
};

export type SearchArticle = {
	id: number;
	title: string;
	departments: string[];
	snippets?: SearchSnippet[];
};

export type SearchDeck = Deck & {
	isOwn: boolean;
	snippets?: SearchSnippet[];
};

export type SearchSection<T> = { count: number; items: T[] };

// decki mają ten sam format co GET /v2/(pes/)flashcards/decks/search
export type SearchDecksSection = Omit<SearchDecksResponse, "items"> & {
	items: SearchDeck[];
	// filtry kategorii z backendu (zawsze z "all")
	availableCategories?: DeckCategoryOption[];
};

export type SearchCounts = Partial<Record<SearchType, number>>;

// z `type` backend dokłada liczniki wszystkich sekcji (dla samej frazy) — do tabów
export type WithCounts<T> = T & { counts?: SearchCounts };

export type SearchResponse = {
	counts?: SearchCounts;
	questions?: SearchSection<SearchQuestion>;
	articles?: SearchSection<SearchArticle>;
	decks?: SearchDecksSection;
};

type SearchParams = {
	searchValue: string;
	type?: SearchType;
	take?: number;
	page?: number;
};

export function search({
	searchValue,
	type,
	take,
	page,
}: SearchParams): Promise<SearchResponse> {
	return http
		.get(`/v2${isPes() ? "/pes" : ""}/search`, {
			params: { ...buildQuery({ searchValue }), type, take, page },
		})
		.then(({ data }) => data);
}

export type CommentsNotificationsFilter = "unread" | "all";

export type QuestionsFilters = Record<string, unknown> & {
	commentsNotifications?: CommentsNotificationsFilter | null;
};

export function searchQuestions(
	{ commentsNotifications, ...filters }: QuestionsFilters,
	{ take = 20, page = 0 }: { take?: number; page?: number } = {},
): Promise<WithCounts<SearchSection<SearchQuestion>>> {
	return http
		.get<SearchResponse>(`/v2${isPes() ? "/pes" : ""}/search`, {
			params: {
				...buildQuery(filters),
				commentsNotifications: commentsNotifications || undefined,
				type: "questions",
				take,
				page,
			},
		})
		.then(({ data }) => ({
			...(data.questions ?? { count: 0, items: [] }),
			counts: data.counts,
		}));
}

export function searchArticles(
	filters: Record<string, unknown>,
	{ take = 20, page = 0 }: { take?: number; page?: number } = {},
): Promise<WithCounts<SearchSection<SearchArticle>>> {
	return http
		.get<SearchResponse>(`/v2${isPes() ? "/pes" : ""}/search`, {
			params: { ...buildQuery(filters), type: "articles", take, page },
		})
		.then(({ data }) => ({
			...(data.articles ?? { count: 0, items: [] }),
			counts: data.counts,
		}));
}

export type DecksSearchParams = {
	take?: number;
	page?: number;
	orderBy?: DeckSortBy;
	order?: DeckSortOrder;
	category?: DeckCategoryFilter;
};

export function searchDecks(
	searchValue: string,
	{ take = 9, page = 0, order, ...params }: DecksSearchParams = {},
): Promise<WithCounts<SearchDecksSection>> {
	return http
		.get<SearchResponse>(`/v2${isPes() ? "/pes" : ""}/search`, {
			params: {
				search: searchValue || undefined,
				type: "decks",
				take,
				page,
				order: order?.toUpperCase(),
				...params,
			},
		})
		.then(({ data }) => ({
			...(data.decks ?? {
				items: [],
				totalCount: 0,
				totalPages: 0,
				page,
				hasNextPage: false,
			}),
			counts: data.counts,
		}));
}
